import React, { useEffect, useState } from "react";
import {
  HierarchyBillingCustomer,
  RetailPlan,
  setAdminBillingPlans,
} from "../../services/billingHierarchyService";

type Props = {
  customer: HierarchyBillingCustomer;
  plans: RetailPlan[];
  onError: (message: string) => void;
  onMessage: (message: string) => void;
};

function money(value: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(Number(value || 0));
}

export default function AdminPlanAccessPanel({
  customer,
  plans,
  onError,
  onMessage,
}: Props) {
  const [selected, setSelected] = useState<string[]>(
    customer.billingRetailPlanIds || [],
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelected(customer.billingRetailPlanIds || []);
  }, [customer.customerId, customer.billingRetailPlanIds]);

  const toggle = (planId: string) => {
    setSelected((current) =>
      current.includes(planId)
        ? current.filter((id) => id !== planId)
        : [...current, planId],
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      await setAdminBillingPlans(customer.customerId, selected);
      onMessage(`Billing plan access saved for ${customer.displayName}.`);
    } catch (error) {
      onError(
        error instanceof Error
          ? error.message
          : "Unable to save Admin billing plan access.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="billing-admin-access">
      {plans.length === 0 ? (
        <span className="billing-muted">
          Create an Integrator customer plan first.
        </span>
      ) : (
        <div className="billing-admin-access__plans">
          {plans.map((plan) => (
            <label
              key={plan.retailPlanId}
              className={`billing-entitlement billing-entitlement--compact ${
                selected.includes(plan.retailPlanId)
                  ? "billing-entitlement--enabled"
                  : ""
              }`}
            >
              <div className="billing-entitlement__title">
                <input
                  type="checkbox"
                  checked={selected.includes(plan.retailPlanId)}
                  onChange={() => toggle(plan.retailPlanId)}
                />
                <strong>{plan.name}</strong>
              </div>
              <span>
                {money(plan.retailMonthlyPrice, plan.currency)} / camera / month
              </span>
            </label>
          ))}
        </div>
      )}

      <div className="billing-admin-access__footer">
        <span>
          {selected.length} of {plans.length} plans available to this Admin
        </span>
        <button
          className="billing-secondary"
          type="button"
          disabled={saving || plans.length === 0}
          onClick={() => void save()}
        >
          {saving ? "Saving…" : "Save Admin plan access"}
        </button>
      </div>
    </div>
  );
}
